import { useEffect, useRef, useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import {
  useMutation,
  useQuery,
  useQueryClient,
  useSuspenseQuery,
} from "@tanstack/react-query";
import { AppShell } from "@/components/AppShell";
import { meQuery, requireAuth } from "@/lib/auth-guard";
import type { UserResponse } from "@/api/auth";
import {
  setSharingPolicy,
  type SharingBucket,
  type SharingPolicyEntry,
} from "@/api/sharing";
import {
  updateFriendCircle,
  type FriendCircle,
  type FriendCirclePolicy,
} from "@/api/friends";
import { friendCirclesQuery, sharingPoliciesQuery } from "@/lib/queries";
import { countLabel } from "@/lib/format";
import {
  Avatar,
  Card,
  EmptyCard,
  ErrorText,
  Hairline,
  LoadingBlock,
  RadioRow,
  SectionLabel,
} from "@/components/ui";

/**
 * Sharing — mirrors iOS SharingScreen ("Family links & friend
 * circles"). Each Extended Family link and each friend circle is an
 * expandable row with the three-bucket radio picker; changes save
 * debounced (400ms, same as iOS). Your policy governs your posts
 * only.
 *
 * Bucket labels are locked vocabulary: Share all / Pick per memory /
 * Off (+ Mirrors guardian, read-only, for wards).
 */

const bucketLabel = (bucket: SharingBucket): string =>
  bucket === "all"
    ? "Share all"
    : bucket === "selected"
      ? "Pick per memory"
      : bucket === "mirror"
        ? "Mirrors guardian"
        : "Off";

const circlePolicyLabel = (policy: FriendCirclePolicy): string =>
  policy === "share_all"
    ? "Share all"
    : policy === "pick_per_memory"
      ? "Pick per memory"
      : "Off";

/** Debounce a save callback — collapse rapid radio taps into one PUT. */
const useDebounced = (fn: () => void, ms: number, deps: unknown[]) => {
  const first = useRef(true);
  useEffect(() => {
    if (first.current) {
      first.current = false;
      return;
    }
    const t = setTimeout(fn, ms);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);
};

const SharingPage = () => {
  const { data: user } = useSuspenseQuery<UserResponse>(meQuery);
  return (
    <AppShell user={user}>
      <SharingView />
    </AppShell>
  );
};

const SharingView = () => {
  const policies = useQuery(sharingPoliciesQuery);
  const circles = useQuery(friendCirclesQuery);
  const [openLink, setOpenLink] = useState<string | null>(null);
  const [openCircle, setOpenCircle] = useState<string | null>(null);

  if (policies.isPending) {
    return <LoadingBlock label="Loading your sharing choices…" />;
  }

  return (
    <div className="mx-auto max-w-2xl">
      <header>
        <h1
          className="font-serif text-[28px] leading-[34px] text-ink"
          style={{ fontWeight: 600 }}
        >
          Sharing
        </h1>
        <p className="mt-1 font-serif text-[14px] italic text-ink-soft">
          Your policy per family link and friend circle. Your choices never
          bind anyone else.
        </p>
      </header>

      <section className="mt-8">
        <SectionLabel>Extended family settings</SectionLabel>
        {policies.isError ? (
          <EmptyCard
            eyebrow="Couldn't load"
            blurb="We couldn't load your sharing choices. Please try again. If this keeps happening, please screenshot this and share with support."
          />
        ) : policies.data.entries.length === 0 ? (
          <EmptyCard
            eyebrow="No links yet"
            title="You're not linked to any Extended Family circles yet."
            blurb="When you link with another family, they'll appear here so you can choose how your memories flow to them."
          />
        ) : (
          <div className="flex flex-col gap-3">
            {policies.data.entries.map((entry) => (
              <LinkPolicyCard
                key={entry.link_id}
                entry={entry}
                open={openLink === entry.link_id}
                onToggle={() =>
                  setOpenLink(
                    openLink === entry.link_id ? null : entry.link_id,
                  )
                }
              />
            ))}
          </div>
        )}
      </section>

      {(circles.data?.length ?? 0) > 0 && (
        <section className="mt-10">
          <SectionLabel>Friend circle settings</SectionLabel>
          <div className="flex flex-col gap-3">
            {circles.data!.map((circle) => (
              <CirclePolicyCard
                key={circle.circle_id}
                circle={circle}
                open={openCircle === circle.circle_id}
                onToggle={() =>
                  setOpenCircle(
                    openCircle === circle.circle_id
                      ? null
                      : circle.circle_id,
                  )
                }
              />
            ))}
          </div>
        </section>
      )}
    </div>
  );
};

// ── Extended Family link policy ─────────────────────────────────────

const LinkPolicyCard = ({
  entry,
  open,
  onToggle,
}: {
  entry: SharingPolicyEntry;
  open: boolean;
  onToggle: () => void;
}) => {
  const queryClient = useQueryClient();
  // `mirror` renders read-only; the editable choices collapse to the
  // three buckets, mapping mirror → off for the initial radio state
  // (same as iOS LinkDetailScreen).
  const isMirror = entry.policy.bucket === "mirror";
  const [bucket, setBucket] = useState<SharingBucket>(
    isMirror ? "off" : entry.policy.bucket,
  );
  const [error, setError] = useState<string | null>(null);
  const name = entry.other_family_name ?? "Another circle";

  const save = useMutation({
    mutationFn: (b: SharingBucket) => setSharingPolicy(entry.link_id, b),
    onSuccess: () => {
      setError(null);
      void queryClient.invalidateQueries({ queryKey: ["sharing-policies"] });
    },
    onError: () => setError("Couldn't update sharing — try again."),
  });

  useDebounced(
    () => {
      if (!isMirror && bucket !== entry.policy.bucket) save.mutate(bucket);
    },
    400,
    [bucket],
  );

  return (
    <Card>
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={open}
        className="tap-target flex w-full items-center gap-4 px-5 py-4 text-left"
      >
        <Avatar name={name} size={38} />
        <span className="min-w-0 flex-1">
          <span className="block truncate font-sans text-[15px] font-medium text-ink">
            {name}
          </span>
          <span className="block font-sans text-[13px] text-taupe-deep">
            {bucketLabel(isMirror ? "mirror" : bucket)}
          </span>
        </span>
        <Chevron open={open} />
      </button>

      {open && (
        <div className="px-5 pb-5">
          <p className="mb-3 font-serif text-[13px] italic leading-[20px] text-ink-soft">
            Your policy governs your posts only. Others in your circle set
            their own.
          </p>
          {isMirror ? (
            <p className="font-sans text-[14px] text-ink-soft">
              Your sharing mirrors your guardian's choice for this link.
            </p>
          ) : (
            <>
              <SectionLabel>What flows from you</SectionLabel>
              <div
                role="radiogroup"
                aria-label={`What flows from you to ${name}`}
                className="overflow-hidden rounded-2xl border border-sand/40"
              >
                <RadioRow
                  label="Share all"
                  sub="All your memories go to this link."
                  selected={bucket === "all"}
                  onSelect={() => setBucket("all")}
                />
                <Hairline />
                <RadioRow
                  label="Pick per memory"
                  sub="Choose which memories go, one at a time, when you post."
                  selected={bucket === "selected"}
                  onSelect={() => setBucket("selected")}
                />
                <Hairline />
                <RadioRow
                  label="Off"
                  sub="Nothing goes to this link."
                  selected={bucket === "off"}
                  onSelect={() => setBucket("off")}
                />
              </div>
              <ErrorText>{error}</ErrorText>
            </>
          )}
        </div>
      )}
    </Card>
  );
};

// ── Friend circle policy ────────────────────────────────────────────

const CirclePolicyCard = ({
  circle,
  open,
  onToggle,
}: {
  circle: FriendCircle;
  open: boolean;
  onToggle: () => void;
}) => {
  const queryClient = useQueryClient();
  const [policy, setPolicy] = useState<FriendCirclePolicy>(
    circle.sharing_policy,
  );
  const [error, setError] = useState<string | null>(null);

  const save = useMutation({
    mutationFn: (p: FriendCirclePolicy) =>
      updateFriendCircle(circle.circle_id, { sharing_policy: p }),
    onSuccess: () => {
      setError(null);
      void queryClient.invalidateQueries({ queryKey: ["friend-circles"] });
    },
    onError: () => setError("Couldn't update sharing — try again."),
  });

  useDebounced(
    () => {
      if (policy !== circle.sharing_policy) save.mutate(policy);
    },
    400,
    [policy],
  );

  return (
    <Card>
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={open}
        className="tap-target flex w-full items-center gap-4 px-5 py-4 text-left"
      >
        <Avatar name={circle.name} size={38} />
        <span className="min-w-0 flex-1">
          <span className="block truncate font-sans text-[15px] font-medium text-ink">
            {circle.name}
          </span>
          <span className="block font-sans text-[13px] text-taupe-deep">
            {circlePolicyLabel(policy)} ·{" "}
            {countLabel(circle.member_count, "friend")}
          </span>
        </span>
        <Chevron open={open} />
      </button>

      {open && (
        <div className="px-5 pb-5">
          <SectionLabel>What flows from you</SectionLabel>
          <div
            role="radiogroup"
            aria-label={`What flows from you to ${circle.name}`}
            className="overflow-hidden rounded-2xl border border-sand/40"
          >
            <RadioRow
              label="Share all"
              sub="All your memories go to this circle."
              selected={policy === "share_all"}
              onSelect={() => setPolicy("share_all")}
            />
            <Hairline />
            <RadioRow
              label="Pick per memory"
              sub="Choose which memories go, one at a time, when you post."
              selected={policy === "pick_per_memory"}
              onSelect={() => setPolicy("pick_per_memory")}
            />
            <Hairline />
            <RadioRow
              label="Off"
              sub="Nothing goes to this circle."
              selected={policy === "share_nothing"}
              onSelect={() => setPolicy("share_nothing")}
            />
          </div>
          <ErrorText>{error}</ErrorText>
        </div>
      )}
    </Card>
  );
};

const Chevron = ({ open }: { open: boolean }) => (
  <svg
    width="20"
    height="20"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.75"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden
    className={`shrink-0 text-taupe-deep transition-transform ${
      open ? "rotate-90" : ""
    }`}
  >
    <path d="M9 6l6 6-6 6" />
  </svg>
);

export const Route = createFileRoute("/sharing")({
  loader: ({ context }) => requireAuth(context.queryClient),
  component: SharingPage,
});
